import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import Toast from "@/components/toast/Toast";
import MaintenanceBanner from "@/components/maintenanceBanner/MaintenanceBanner";
import "swiper/css";

import { GamesProvider } from "./providers/GamesProvider";
import { ConfigProvider } from "@/providers/ConfigProvider";
import { AuthProvider } from "@/providers/AuthProvider";
import { LobbyConfigProvider } from "@/providers/LobbyConfigProvider";
import { useAutoResizeIframe } from "./hooks/useAutoResizeIframe";
import CasinoSkeletonLayout from "@/components/casinoSkeleton/CasinoSkeletonLayout";
import RankCardSkeleton from "@/widgets/rankCardWidget/RankCardSkeleton";
import RankCardError from "@/widgets/rankCardWidget/RankCardError";
import WidgetErrorBoundary from "@/components/errorBoundary/WidgetErrorBoundary";
import SentryRouteTag from "@/components/errorBoundary/SentryRouteTag";

const ShopWidget = lazy(() => import("./modules/shopModule/ShopWidget"));
const CasinoWidgetGuard = lazy(() => import("@/widgets/casinoWidget/CasinoWidgetGuard"));
const JackpotIframeGuard = lazy(
  () => import("@/widgets/casinoWidget/subWidgets/jackpotWidget/iframe/JackpotIframeGuard")
);
const WidgetApp = lazy(() => import("@/widgets/widgetApp/WidgetApp"));
const MissionsWidget = lazy(
  () => import("@/widgets/casinoWidget/subWidgets/missionsWidget/MissionsWidget")
);
const LobbyWidget = lazy(() => import("@/widgets/lobbyWidget/LobbyWidget"));
const CasinoHubWidget = lazy(() => import("@/widgets/casinoWidget/casinoHub/CasinoHubWidget"));
const LoyaltyWidget = lazy(() => import("@/widgets/casinoWidget/loyalty/Loyalty"));
const RankCardWidget = lazy(() => import("@/widgets/rankCardWidget/RankCardWidget"));

const CompletionWidget = lazy(() => import("@/widgets/completionWidget/CompletionWidget"));
const PromoCenterWidget = lazy(
  () => import("@/widgets/casinoWidget/subWidgets/promoCenterWidget/iframe/PromoCenterWidget")
);
const PromoCenterDemoPage = lazy(
  () => import("@/widgets/casinoWidget/subWidgets/promoCenterWidget/demo/PromoCenterDemoPage")
);

const App = () => {
  useAutoResizeIframe();
  const F2P_LOBBY_CONFIG_ROUTE = `lobby/config`;
  const CASINO_MISSIONS_LOBBY_CONFIG_ROUTE = `lobby/config/mission`;
  const CASINO_HUB_LOBBY_CONFIG_ROUTE = `lobby/config/casino`;
  const CASINO_SHOP_LOBBY_CONFIG_ROUTE = `lobby/config/shop`;
  const CASINO_RANK_MISSIONS = `lobby/config/rank`;
  const BADGES_LOBBY_CONFIG_ROUTE = `lobby/config/badge`;

  return (
    <div className="app">
      <WidgetErrorBoundary>
        <Toast />
        <MaintenanceBanner />
        <AuthProvider>
          <BrowserRouter>
            <SentryRouteTag />
            <Routes>
              {/* Soccerbet-specific surface — added as a temporary feature request. */}
              <Route
                path="/casino/completion"
                element={
                  <Suspense fallback={null}>
                    <CompletionWidget />
                  </Suspense>
                }
              />
              <Route path="/f2p/*">
                <Route
                  path="lobby"
                  element={
                    <Suspense fallback={null}>
                      <LobbyConfigProvider dataRoute={F2P_LOBBY_CONFIG_ROUTE}>
                        <LobbyWidget />
                      </LobbyConfigProvider>
                    </Suspense>
                  }
                />
                <Route
                  path="game/*"
                  element={
                    <Suspense fallback={null}>
                      <ConfigProvider>
                        <WidgetApp />
                      </ConfigProvider>
                    </Suspense>
                  }
                />
              </Route>
              <Route path="/casino/*">
                <Route
                  path="game"
                  element={
                    <Suspense fallback={<CasinoSkeletonLayout />}>
                      <CasinoWidgetGuard />
                    </Suspense>
                  }
                ></Route>
                <Route
                  path="jackpot/cards"
                  element={
                    <Suspense fallback={null}>
                      <JackpotIframeGuard mode="cards" />
                    </Suspense>
                  }
                />
                <Route
                  path="jackpot/fullscreen"
                  element={
                    <Suspense fallback={null}>
                      <JackpotIframeGuard mode="fullscreen" />
                    </Suspense>
                  }
                />
                <Route
                  path="game-studio"
                  element={
                    <Suspense fallback={null}>
                      <PromoCenterWidget />
                    </Suspense>
                  }
                />
                <Route
                  path="game-studio/demo"
                  element={
                    <Suspense fallback={null}>
                      <PromoCenterDemoPage />
                    </Suspense>
                  }
                />
                <Route
                  path="missions"
                  element={
                    <Suspense fallback={null}>
                      <LobbyConfigProvider dataRoute={CASINO_MISSIONS_LOBBY_CONFIG_ROUTE}>
                        <GamesProvider>
                          <MissionsWidget />
                        </GamesProvider>
                      </LobbyConfigProvider>
                    </Suspense>
                  }
                ></Route>
              </Route>
              <Route
                path="/lobby/casino"
                element={
                  <Suspense fallback={null}>
                    <LobbyConfigProvider dataRoute={CASINO_HUB_LOBBY_CONFIG_ROUTE}>
                      <GamesProvider>
                        <CasinoHubWidget />
                      </GamesProvider>
                    </LobbyConfigProvider>
                  </Suspense>
                }
              ></Route>
              <Route
                path="/lobby/loyalty"
                element={
                  <Suspense fallback={null}>
                    <LobbyConfigProvider dataRoute={CASINO_RANK_MISSIONS}>
                      <GamesProvider>
                        <LoyaltyWidget />
                      </GamesProvider>
                    </LobbyConfigProvider>
                  </Suspense>
                }
              ></Route>
              <Route
                path="/lobby/rank-card"
                element={
                  <Suspense fallback={<RankCardSkeleton />}>
                    <LobbyConfigProvider
                      dataRoute={CASINO_RANK_MISSIONS}
                      loadingFallback={<RankCardSkeleton />}
                      errorFallback={<RankCardError />}
                    >
                      <RankCardWidget />
                    </LobbyConfigProvider>
                  </Suspense>
                }
              ></Route>
              <Route
                path="/casino/shop"
                element={
                  <Suspense fallback={null}>
                    <LobbyConfigProvider dataRoute={CASINO_SHOP_LOBBY_CONFIG_ROUTE}>
                      <GamesProvider>
                        <ShopWidget />
                      </GamesProvider>
                    </LobbyConfigProvider>
                  </Suspense>
                }
              ></Route>
              <Route
                path="/lobby/badges"
                element={
                  <Suspense fallback={null}>
                    <LobbyConfigProvider dataRoute={BADGES_LOBBY_CONFIG_ROUTE}>
                      <CasinoWidgetGuard mode="badgeEndless" />
                    </LobbyConfigProvider>
                  </Suspense>
                }
              ></Route>
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </WidgetErrorBoundary>
    </div>
  );
};

export default App;
